import * as React from 'react';

import enterpriseAdminUser from 'bundles/enterprise-admin';
import type { SortBy } from 'bundles/enterprise-admin-messages/types';
import { SortByDirection } from 'bundles/enterprise-admin-messages/types';
import type { PrimaryFilterType } from 'bundles/enterprise-admin-user-filters/types';
import SortableTableHeader from 'bundles/enterprise-admin-users/components/SortableTableHeader';
import {
  renderBoolean,
  renderBusinessUnitCell,
  renderDateTime,
  renderDetailsButton,
  renderFlags,
  renderLearner,
  renderLearnerEffort,
  renderLeftAlignedNumberCell,
  renderLocationCell,
  renderManagerCell,
  renderMultipleEntries,
  renderNumberCell,
  renderPercentCell,
  renderStandardCell,
  renderStatus,
} from 'bundles/enterprise-admin-users/components/TableCellRenderers';
import TableRowActions from 'bundles/enterprise-admin-users/components/TableRowActions';
import { AttributeSelections } from 'bundles/enterprise-admin-users/types';
import { isAnonymizedUser } from 'bundles/enterprise-admin-users/utils';
import LabelWithContext from 'bundles/enterprise-ui/components/LabelWithContext';

import _t from 'i18n!nls/enterprise-admin-users';

const renderCellActions =
  ({
    allowDeletion,
    allowSendMessage,
    disableEditProfile,
    primaryFilterType,
    onRemoveInvitation,
    onRemoveLearner,
    onEditProfile,
    onSendMessageToLearner,
    onAssignContent,
  }: {
    allowDeletion: boolean;
    allowSendMessage: boolean;
    disableEditProfile?: boolean;
    primaryFilterType?: PrimaryFilterType;
    onRemoveInvitation: (id: string) => void;
    onRemoveLearner: (id: string) => void;
    onEditProfile: (userIdentifier: string) => void;
    onSendMessageToLearner: (id: string) => void;
    onAssignContent?: (id: string) => void;
  }) =>
  (userIdentifier: string, row: $TSFixMe, rowIndex: number) => {
    let isAnonymized;
    try {
      const { name, email } = row.learner;
      isAnonymized = isAnonymizedUser({ name, email });
    } catch {
      isAnonymized = false;
    }

    if (isAnonymized) {
      return null;
    }

    return (
      <div css={{ position: 'relative' }}>
        <div
          css={{
            position: 'absolute',
            right: '11px', // padding from cui table
            top: '-13px', // half the height of the button, cannot use a transform because of fixed-positioned children
            background: rowIndex % 2 ? '#fff' : 'rgb(251, 251, 251)',
          }}
        >
          <TableRowActions
            onSendMessage={onSendMessageToLearner}
            userIdentifier={userIdentifier}
            primaryFilterType={primaryFilterType}
            allowDeletion={allowDeletion}
            allowSendMessage={allowSendMessage}
            disableEditProfile={disableEditProfile}
            onEditProfile={onEditProfile}
            onRemoveInvitation={() => onRemoveInvitation(userIdentifier)}
            onRemoveLearner={() => onRemoveLearner(userIdentifier)}
            onAssignContent={onAssignContent ? () => onAssignContent(userIdentifier) : undefined}
          />
        </div>
      </div>
    );
  };

export const getUsersListColumns = ({
  enableEnhancedUserFilters,
  allowDeletion,
  allowSendMessage,
  disableEditProfile,
  disableDeepViewModal,
  showDetailsButton,
  multipleProgramsSelected,
  sortBy,
  selectedColumns,
  primaryFilterType,
  isProgramDomainAdmin,
  onSortChange,
  onUserClick,
  onRemoveInvitation,
  onRemoveLearner,
  onEditProfile,
  onSendMessageToLearner,
  onAssignContent,
}: {
  enableEnhancedUserFilters?: boolean;
  allowDeletion: boolean;
  allowSendMessage: boolean;
  disableEditProfile?: boolean;
  disableDeepViewModal?: boolean;
  showDetailsButton?: boolean;
  multipleProgramsSelected: boolean;
  sortBy: SortBy[];
  selectedColumns: AttributeSelections[];
  primaryFilterType?: PrimaryFilterType;
  isProgramDomainAdmin: boolean;
  onSortChange: (sortBy: SortBy[]) => void;
  onUserClick?: (index: number) => void;
  onRemoveInvitation: (id: string) => void;
  onRemoveLearner: (id: string) => void;
  onEditProfile: (userIdentifier: string) => void;
  onSendMessageToLearner: (id: string) => void;
  onAssignContent?: (id: string) => void;
}) => {
  const isFreemiumOrg = enterpriseAdminUser.isFreemiumOrg || enterpriseAdminUser.isC4CBOrg;
  const allowTimestampSorting = !(multipleProgramsSelected && isProgramDomainAdmin);
  const allowSortingByInvitation = allowTimestampSorting && primaryFilterType === 'INVITED';
  const allowSortingByEnrollmentsAndCompletions = !isFreemiumOrg && !(multipleProgramsSelected && isProgramDomainAdmin);

  const handleSortChange = (column: AttributeSelections) => {
    const currentColumnSortBy = sortBy.find((sortByItem) => sortByItem.column === column);
    if (currentColumnSortBy && currentColumnSortBy.direction === SortByDirection.desc) {
      onSortChange([
        {
          column,
          direction: SortByDirection.asc,
        },
      ]);
    } else {
      onSortChange([
        {
          column,
          direction: SortByDirection.desc,
        },
      ]);
    }
  };
  let numberOfEnrollmentsHeader: React.ReactNode = _t('# Enrollments');
  const numberOfEnrollmentsContext = _t(
    'This displays the overall number of course and guided project enrollments that this learner has had since your organization started using Coursera.'
  );
  if (allowSortingByEnrollmentsAndCompletions) {
    numberOfEnrollmentsHeader = (
      <SortableTableHeader
        sortBy={sortBy}
        label={<LabelWithContext label={_t('# Enrollments')} context={numberOfEnrollmentsContext} />}
        column={AttributeSelections.numEnrollments}
        onSortChange={handleSortChange}
      />
    );
  }

  const columns = [
    {
      accessor: 'learner',
      header: (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Learner')}
          column={AttributeSelections.learner}
          onSortChange={handleSortChange}
        />
      ),
      cellRenderer: disableDeepViewModal ? renderLearner() : renderLearner(onUserClick),
      width: '200px',
    },
    {
      accessor: 'joinedAt',
      header: allowTimestampSorting ? (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Join Date')}
          column={AttributeSelections.joinedAt}
          onSortChange={handleSortChange}
        />
      ) : (
        _t('Join Date')
      ),
      cellRenderer: renderDateTime,
      width: '155px',
    },
    {
      accessor: 'invitedAt',
      header: allowSortingByInvitation ? (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Invitation Date')}
          column={AttributeSelections.invitedAt}
          onSortChange={handleSortChange}
        />
      ) : (
        _t('Invitation Date')
      ),
      cellRenderer: renderDateTime,
      width: '155px',
    },
    {
      accessor: 'deletedAt',
      header: allowTimestampSorting ? (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Removal date')}
          column={AttributeSelections.deletedAt}
          onSortChange={handleSortChange}
        />
      ) : (
        _t('Removal date')
      ),
      cellRenderer: renderDateTime,
      width: '155px',
    },
    {
      accessor: 'grade',
      header: (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Grade')}
          column={AttributeSelections.grade}
          onSortChange={handleSortChange}
        />
      ),
      cellRenderer: renderPercentCell,
      width: '100px',
    },
    {
      accessor: 'progressPercentage',
      header: (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Progress')}
          column={AttributeSelections.progressPercentage}
          onSortChange={handleSortChange}
        />
      ),
      cellRenderer: renderPercentCell,
      width: '155px',
    },
    {
      accessor: 'plagiarismCount',
      header: (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Plagiarism Incidents')}
          column={AttributeSelections.plagiarismCount}
          onSortChange={handleSortChange}
        />
      ),
      cellRenderer: renderNumberCell,
      width: '155px',
    },
    {
      accessor: 'enrolledAt',
      header: allowTimestampSorting ? (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Enrollment Date')}
          column={AttributeSelections.enrolledAt}
          onSortChange={handleSortChange}
        />
      ) : (
        _t('Enrollment Date')
      ),
      cellRenderer: renderDateTime,
      width: '155px',
    },
    {
      accessor: 'completedAt',
      header: allowTimestampSorting ? (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Completion Date')}
          column={AttributeSelections.completedAt}
          onSortChange={handleSortChange}
        />
      ) : (
        _t('Completion Date')
      ),
      cellRenderer: renderDateTime,
      width: '155px',
    },
    {
      accessor: 'latestActivityAt',
      header: allowTimestampSorting ? (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Latest Program Activity Date')}
          column={AttributeSelections.latestActivityAt}
          onSortChange={handleSortChange}
        />
      ) : (
        _t('Latest Program Activity Date')
      ),
      cellRenderer: (timestamp?: string | undefined) => (timestamp ? renderDateTime(timestamp) : _t('No activity')),
      width: '170px',
    },
    {
      accessor: 'lastActivityAt',
      header: allowTimestampSorting ? (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Last Activity')}
          column={AttributeSelections.latestActivityAt}
          onSortChange={handleSortChange}
        />
      ) : (
        _t('Latest Activity Date')
      ),
      cellRenderer: (timestamp?: string | undefined) => (timestamp ? renderDateTime(timestamp) : _t('No activity')),
      width: '170px',
    },
    {
      accessor: 'plagiarismIncidents',
      header: (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Plagiarism incidents')}
          column={AttributeSelections.plagiarismIncidents}
          onSortChange={handleSortChange}
        />
      ),
      cellRenderer: renderLeftAlignedNumberCell,
      width: '275px',
    },
    {
      accessor: 'appealsGranted',
      header: (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Appeals granted')}
          column={AttributeSelections.appealsGranted}
          onSortChange={handleSortChange}
        />
      ),
      cellRenderer: renderLeftAlignedNumberCell,
      width: '275px',
    },
    {
      accessor: 'riskFlagsCount',
      header: (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Flags')}
          column={AttributeSelections.riskFlagsCount}
          onSortChange={handleSortChange}
        />
      ),
      cellRenderer: renderFlags,
      width: '170px',
    },
    {
      accessor: 'learnerEffort',
      header: (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Learner effort')}
          column={AttributeSelections.learnerEffort}
          onSortChange={handleSortChange}
        />
      ),
      cellRenderer: renderLearnerEffort,
      width: '140px',
    },
    {
      accessor: 'courseLevelRiskFlagsCount',
      header: (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Flags')}
          column={AttributeSelections.courseLevelRiskFlagsCount}
          onSortChange={handleSortChange}
        />
      ),
      cellRenderer: renderFlags,
      width: '170px',
    },
    {
      accessor: 'status',
      header: _t('Status'),
      cellRenderer: renderStatus,
      width: '170px',
    },
    {
      accessor: 'programNames',
      header: _t('Programs'),
      cellRenderer: renderMultipleEntries,
      width: '345px',
    },
    {
      accessor: 'contractNames',
      header: _t('Contracts'),
      cellRenderer: renderMultipleEntries,
      width: '345px',
    },
    {
      accessor: 'productNames',
      header: _t('Current Enrollments'),
      cellRenderer: renderMultipleEntries,
      width: '345px',
    },
    {
      accessor: 'numEnrollments',
      header: numberOfEnrollmentsHeader,
      cellRenderer: renderNumberCell,
      width: '135px',
    },
    {
      accessor: 'numCompletions',
      header: allowSortingByEnrollmentsAndCompletions ? (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('# Completions')}
          column={AttributeSelections.numCompletions}
          onSortChange={handleSortChange}
        />
      ) : (
        _t('# Completions')
      ),
      cellRenderer: renderNumberCell,
      width: '135px',
    },
    {
      accessor: 'externalId',
      header: _t('External ID'),
      cellRenderer: renderStandardCell,
      width: '200px',
    },
    {
      accessor: 'isSoftDeleted',
      header: (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Allow return')}
          column={AttributeSelections.isSoftDeleted}
          onSortChange={handleSortChange}
        />
      ),
      cellRenderer: renderBoolean,
      width: '155px',
    },
    {
      accessor: 'businessUnits',
      header: _t('Business units'),
      cellRenderer: renderBusinessUnitCell,
      width: '120px',
    },
    {
      accessor: 'jobTitle',
      header: _t('Job title'),
      cellRenderer: renderStandardCell,
      width: '100px',
    },
    {
      accessor: 'location',
      header: _t('Location'),
      cellRenderer: renderLocationCell,
      width: '150px',
    },
    {
      accessor: 'manager',
      header: _t('Manager'),
      cellRenderer: renderManagerCell,
      width: '200px',
    },
    {
      accessor: 'jobType',
      header: _t('Job type'),
      cellRenderer: renderStandardCell,
      width: '100px',
    },
    {
      accessor: 'isLearnerRemoved',
      header: (
        <SortableTableHeader
          sortBy={sortBy}
          label={_t('Learner Removed')}
          column={AttributeSelections.isLearnerRemoved}
          onSortChange={handleSortChange}
        />
      ),
      cellRenderer: renderBoolean,
      width: '100px',
    },
  ];
  const filteredColumns = columns.filter(({ accessor }) => {
    const column = AttributeSelections[accessor as keyof typeof AttributeSelections];
    return selectedColumns.includes(column);
  });
  if (showDetailsButton) {
    return [
      ...filteredColumns,
      {
        accessor: 'actions',
        header: '',
        cellRenderer: renderDetailsButton(onUserClick),
        width: '71px',
      },
    ];
  } else if (allowDeletion || allowSendMessage) {
    if (enableEnhancedUserFilters) {
      return [
        ...filteredColumns.slice(0, 1),
        {
          accessor: 'actions',
          header: '',
          cellRenderer: renderCellActions({
            allowDeletion,
            allowSendMessage,
            disableEditProfile,
            onRemoveInvitation,
            onRemoveLearner,
            onEditProfile,
            onSendMessageToLearner,
            onAssignContent,
            primaryFilterType,
          }),
          width: '150px',
        },
        ...filteredColumns.slice(1),
      ];
    } else {
      return [
        ...filteredColumns,
        {
          accessor: 'actions',
          header: '',
          cellRenderer: renderCellActions({
            allowDeletion,
            allowSendMessage,
            disableEditProfile,
            onRemoveInvitation,
            onRemoveLearner,
            onEditProfile,
            onSendMessageToLearner,
            onAssignContent,
            primaryFilterType,
          }),
          width: '50px',
        },
      ];
    }
  } else {
    return filteredColumns;
  }
};
